import React, { useState } from 'react';
import { Mail, User, Briefcase, Users, CheckCircle } from 'lucide-react';

// Launch-notification capture. Same waitlist endpoint the demo forms use, with
// waitlist_type "healthcare", so signups show up in the daily digest.
const NotifyMe = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [size, setSize] = useState('solo');
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(false);
    try {
      const r = await fetch('https://app.getsortio.com/api/sortio-waitlist/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          waitlist_type: 'healthcare',
          name: org ? `${name} (${org})` : name,
          email,
          company_size: size,
        }),
      });
      if (r.ok) setSubmitted(true);
      else setError(true);
    } catch {
      setError(true);
    }
    setBusy(false);
  };

  const field = 'w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all';

  return (
    <section id="notify" className="my-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          Get notified at <span className="apple-gradient-text">launch</span>
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">
          One email when there is something real to try. Nothing else.
        </p>
      </div>
      <div className="bg-apple-700/30 backdrop-blur-md p-8 sm:p-10 rounded-3xl border border-apple-600/30 relative overflow-hidden max-w-2xl mx-auto">
        {!submitted ? (
          <form onSubmit={submit} className="relative z-10">
            <div className="grid sm:grid-cols-2 gap-5 mb-5">
              <label className="block">
                <span className="block text-sm font-medium mb-2 text-apple-100">Name</span>
                <div className="relative">
                  <User className="absolute left-4 top-3.5 text-apple-400" size={16} />
                  <input type="text" className={field} placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
                </div>
              </label>
              <label className="block">
                <span className="block text-sm font-medium mb-2 text-apple-100">Email</span>
                <div className="relative">
                  <Mail className="absolute left-4 top-3.5 text-apple-400" size={16} />
                  <input type="email" className={field} placeholder="you@practice.com" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={254} />
                </div>
              </label>
              <label className="block">
                <span className="block text-sm font-medium mb-2 text-apple-100">Practice or organization <span className="text-apple-400 text-xs">(optional)</span></span>
                <div className="relative">
                  <Briefcase className="absolute left-4 top-3.5 text-apple-400" size={16} />
                  <input type="text" className={field} placeholder="Riverside Pediatrics" value={org} onChange={(e) => setOrg(e.target.value)} maxLength={120} />
                </div>
              </label>
              <label className="block">
                <span className="block text-sm font-medium mb-2 text-apple-100">Team size</span>
                <div className="relative">
                  <Users className="absolute left-4 top-3.5 text-apple-400" size={16} />
                  <select className={`${field} appearance-none`} value={size} onChange={(e) => setSize(e.target.value)}>
                    <option value="solo">Solo</option>
                    <option value="2-10">2-10</option>
                    <option value="11-50">11-50</option>
                    <option value="50+">50+</option>
                  </select>
                </div>
              </label>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-gradient-to-r from-sortio-primary to-sortio-primary/80 text-white font-medium px-8 py-4 text-base rounded-xl shadow-lg shadow-sortio-primary/20 hover:brightness-110 transition-all duration-300 disabled:opacity-60"
            >
              {busy ? 'Sending...' : 'Notify me'}
            </button>
            {error && (
              <p className="mt-4 text-sm text-red-300">Something went wrong. Please try again or email marcus@getsortio.com.</p>
            )}
            <p className="text-apple-400 text-xs text-center mt-4">
              We use this only to tell you when the healthcare plan is ready.
            </p>
          </form>
        ) : (
          <div className="text-center py-8 relative z-10">
            <div className="w-20 h-20 rounded-full bg-sortio-secondary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-sortio-secondary" />
            </div>
            <h3 className="text-2xl font-medium mb-3 text-apple-50">You are on the list</h3>
            <p className="text-apple-300">We will email you once when there is something to try.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default NotifyMe;
